import { addClass, addEventListenerIfNotExists, disable, enable, getApiUrl, getCurrentLanguageId, getPopup, removeClass } from "./common";
import HttpClient from "./http-client";
import LoadingIndication from "./loading-indication";
import { PopupOptions } from "./popup";
import { suppressFirstVisitorPopup } from "./first-visitor-suppression";

export default class FirstVisitorPopup{
    baseElement: HTMLElement;
    emailInput: HTMLInputElement;
    submitBtn: HTMLInputElement;
    notInterestedBtn: HTMLInputElement;
    errosSection: HTMLElement;
    successSection: HTMLElement;
    mainSection: HTMLElement;

    constructor() {
        this.initElements();
        this.registerEventListeners();
    }

    private initElements(): void {
        this.baseElement = document.querySelector('[data-component="FirstVisitorPopup"]');
        this.emailInput = this.baseElement.querySelector('#first-visitor-email-input');
        this.submitBtn = this.baseElement.querySelector('#first-visitor-submit-btn');
        this.notInterestedBtn = this.baseElement.querySelector('#first-visitor-notinterested-btn');
        this.errosSection = this.baseElement.querySelector('#first-visitor-errors');
        this.successSection = this.baseElement.querySelector('#first-visitor-success');
        this.mainSection = this.baseElement.querySelector('#first-visitor-main');
        this.disableHtmlElement(this.submitBtn);
    }

    private registerEventListeners(): void {
        addEventListenerIfNotExists(this.submitBtn, 'click', async (event: Event) => {
            await this.requestDiscountCoupon();
        });

        addEventListenerIfNotExists(this.notInterestedBtn, 'click', async (event: Event) => {
            sessionStorage.setItem('first-visitor-popup-disable', 'true');
            suppressFirstVisitorPopup('dismissed');
            this.closePopup();
        });

        addEventListenerIfNotExists(this.emailInput, 'keypress', async (event: KeyboardEvent) => {
            if (event.key == 'Enter' && this.emailInputNotEmpty()) {
                await this.requestDiscountCoupon();
            }
        });

        addEventListenerIfNotExists(this.emailInput, 'input', (event: InputEvent) => {
            if (this.emailInputNotEmpty()) {
                this.enableHtmlElement(this.submitBtn);
            } else {
                this.disableHtmlElement(this.submitBtn);
            }
        });
    }

    private async requestDiscountCoupon(): Promise<void> {
        this.showLoadingIndication();
        await this.sendDiscountCoupon(this.emailInput.value);
        this.hideLoadingIndication();
    }

    private async sendDiscountCoupon(email: string): Promise<void> {
        const client = new HttpClient();
        const url = `${getApiUrl()}/coupon/send-first-visitor-coupon`;

        const response = await client.fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json;',
                'Current-Language-Id': getCurrentLanguageId()
            },
            body: JSON.stringify({
                email: email.trim()
            })
        }, async (response: Response) => {
            this.clearAndHideErrorMessages();
            if (response.status == 500) {
                this.addErrorMessages([`There was an error when applying coupon. Please contact eshop owner. (Status: ${response.status} Status Text: ${response.statusText})`]);
            } else {
                const responseObject = await response.json() as FirstVisitorCouponResponse;
                this.addErrorMessages(responseObject.messages);
            }
            this.showCouponsErrorsSection();
        });

        if (response.ok) {
            this.clearAndHideErrorMessages();
            this.showSuccessSection();
            this.hideMainSection();
            sessionStorage.setItem('first-visitor-popup-disable', 'true');
            suppressFirstVisitorPopup('done');
        }
    }

    private emailInputNotEmpty(): boolean {
        return this.emailInput.value.trim() !== '';
    }

    private enableHtmlElement(element: HTMLElement): void {
        element.style.pointerEvents = "auto";
        removeClass(element, 'disabled');
        enable(element);
    }

    private disableHtmlElement(element: HTMLElement): void {
        element.style.pointerEvents = "none";
        addClass(element, 'disabled');
        disable(element);
    }

    private showLoadingIndication(): void {
        LoadingIndication.show(this.baseElement);
    }

    private hideLoadingIndication(): void {
        LoadingIndication.hide(this.baseElement);
    }

    private hideMainSection(): void {
        this.mainSection.style.display = 'none';
    }

    private showSuccessSection(): void {
        this.successSection.style.display = 'block';
    }

    private showCouponsErrorsSection(): void {
        this.errosSection.style.display = 'block';
    }

    private addErrorMessages(errorMessages: string[]): void {
        errorMessages.forEach(message => {
            const item = document.createElement('li');
            item.textContent = message;
            this.errosSection.appendChild(item);
        });
    }

    private clearAndHideErrorMessages(): void {
        this.errosSection.innerHTML = '';
        this.errosSection.style.display = 'none';
    }

    private closePopup(): void {
        const popup = getPopup(<PopupOptions>{ id: 'first-visitor-popup' });
        popup.close();
    }
}

interface FirstVisitorCouponResponse {
    messages: string[];
}

